import * as Icons from "lucide-react";
import { ChevronRight, SquareArrowOutUpRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
} from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/core/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { AppVersionLabel } from "@/core/components/shared/app-version-label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/core/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import {
  filterNavItemsByRoles,
  navItems,
  type NavGroup,
  type NavLeafSubItem,
  type NavMenuItem,
  type NavSubItem,
  type NavGroupSubItem,
} from "@/core/config/nav-config";
import { useRealmRoles } from "@/core/auth/roles";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import { getDisplayAppVersion } from "@/core/utils/func/constants";

type SearchParams = Pick<URLSearchParams, "get">;
type Translate = (
  key: TFunctionType,
  params?: Record<string, unknown>
) => string;

const isSubGroup = (item: NavSubItem): item is NavGroupSubItem =>
  "childItems" in item;

const isExternalLink = (href: string) =>
  href.startsWith("http://") || href.startsWith("https://");

const resolveIcon = (item: NavMenuItem) =>
  item.icon
    ? (Icons[item.icon] as unknown as ComponentType<{ className?: string }>)
    : null;

// Clé d'état d'ouverture d'un sous-groupe imbriqué, namespacée par son parent.
const subGroupKey = (itemLabel: string, subItemLabel: string) =>
  `${itemLabel}::${subItemLabel}`;

function isLinkActive(
  href: string,
  activePath: string | undefined,
  pathname: string,
  searchParams: SearchParams
): boolean {
  if (activePath) {
    return pathname.startsWith(activePath);
  }

  if (href.includes("?")) {
    const [hrefPath, hrefQuery] = href.split("?");

    if (pathname !== hrefPath) return false;

    const hrefParams = new URLSearchParams(hrefQuery);

    for (const [key, value] of hrefParams.entries()) {
      if (searchParams.get(key) !== value) return false;
    }

    return true;
  }

  return pathname === href;
}

// Branches contenant la route active : l'arbre s'ouvre tout seul au premier
// rendu et suit la navigation, sauf si l'utilisateur a explicitement basculé.
function getActiveBranchKeys(
  groups: NavGroup[],
  pathname: string,
  searchParams: SearchParams
): Set<string> {
  const keys = new Set<string>();

  groups.forEach((group) => {
    group.items.forEach((item) => {
      item.childItems?.forEach((subItem) => {
        if (isSubGroup(subItem)) {
          if (
            subItem.childItems.some((leaf) =>
              isLinkActive(leaf.href, leaf.activePath, pathname, searchParams)
            )
          ) {
            keys.add(item.label);
            keys.add(subGroupKey(item.label, subItem.label));
          }
        } else if (
          isLinkActive(subItem.href, subItem.activePath, pathname, searchParams)
        ) {
          keys.add(item.label);
        }
      });
    });
  });

  return keys;
}

// Lien navigable dans le flyout du mode icône (rail trop étroit pour le sous-menu inline).
const FlyoutMenuLink = ({
  item,
  isActive,
  t,
}: {
  item: NavLeafSubItem;
  isActive: boolean;
  t: Translate;
}) => (
  <DropdownMenuItem
    asChild
    className={cn(
      "gap-2",
      isActive && "bg-primary/10 font-medium text-accent-foreground"
    )}
  >
    <Link to={item.href} target={item.target}>
      <span className="truncate">{t(item.label)}</span>
      {isExternalLink(item.href) && (
        <SquareArrowOutUpRight className="ml-auto size-3.5 shrink-0 opacity-50" />
      )}
      {item.badge && (
        <span
          className={cn(
            "ml-auto rounded-full bg-primary/10 px-1.5 text-xs font-normal",
            item.badgeClassName
          )}
        >
          {item.badge}
        </span>
      )}
    </Link>
  </DropdownMenuItem>
);

// Équivalent flyout d'un item repliable quand la sidebar est en rail icône :
// les enfants s'ouvrent dans un dropdown à droite du rail.
const FlyoutMenuItem = ({
  item,
  childItems,
  isChildActive,
  pathname,
  searchParams,
  t,
}: {
  item: NavMenuItem;
  childItems: NavSubItem[];
  isChildActive: boolean;
  pathname: string;
  searchParams: SearchParams;
  t: Translate;
}) => {
  const Tag = resolveIcon(item);

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            isActive={isChildActive}
            className="data-[active=true]:bg-primary/5"
          >
            {Tag && <Tag />}
            <span>{t(item.label)}</span>
            <ChevronRight className="ml-auto" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-auto min-w-52"
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-sm text-foreground">
              {t(item.label)}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {childItems.map((subItem) =>
              isSubGroup(subItem) ? (
                <DropdownMenuSub key={subItem.label}>
                  <DropdownMenuSubTrigger
                    className={cn(
                      subItem.childItems.some((leaf) =>
                        isLinkActive(
                          leaf.href,
                          leaf.activePath,
                          pathname,
                          searchParams
                        )
                      ) && "bg-primary/10 font-medium text-accent-foreground"
                    )}
                  >
                    <span className="truncate">{t(subItem.label)}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    sideOffset={9}
                    className="w-auto min-w-48"
                  >
                    {subItem.childItems.map((leaf) => (
                      <FlyoutMenuLink
                        key={leaf.label}
                        item={leaf}
                        isActive={isLinkActive(
                          leaf.href,
                          leaf.activePath,
                          pathname,
                          searchParams
                        )}
                        t={t}
                      />
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <FlyoutMenuLink
                  key={subItem.label}
                  item={subItem}
                  isActive={isLinkActive(
                    subItem.href,
                    subItem.activePath,
                    pathname,
                    searchParams
                  )}
                  t={t}
                />
              )
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

const SidebarGroupedMenuItems = ({
  data,
  groupLabel,
  pathname,
  searchParams,
  isIconMode,
  isBranchOpen,
  setOpenItem,
  t,
}: {
  data: NavMenuItem[];
  groupLabel?: TFunctionType;
  pathname: string;
  searchParams: SearchParams;
  isIconMode: boolean;
  isBranchOpen: (key: string) => boolean;
  setOpenItem: (key: string, open: boolean) => void;
  t: Translate;
}) => {
  return (
    <SidebarGroup>
      {groupLabel && (
        <SidebarGroupLabel className="uppercase tracking-wider text-sidebar-foreground/50">
          {t(groupLabel)}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu>
          {data.map((item) => {
            const Tag = resolveIcon(item);

            const isChildActive =
              item.childItems?.some((subItem) =>
                isSubGroup(subItem)
                  ? subItem.childItems.some((leaf) =>
                      isLinkActive(
                        leaf.href,
                        leaf.activePath,
                        pathname,
                        searchParams
                      )
                    )
                  : isLinkActive(
                      subItem.href,
                      subItem.activePath,
                      pathname,
                      searchParams
                    )
              ) ?? false;

            if (item.childItems && isIconMode) {
              return (
                <FlyoutMenuItem
                  key={item.label}
                  item={item}
                  childItems={item.childItems}
                  isChildActive={isChildActive}
                  pathname={pathname}
                  searchParams={searchParams}
                  t={t}
                />
              );
            }

            return item.childItems ? (
              <Collapsible
                className="group/collapsible"
                key={item.label}
                open={isBranchOpen(item.label)}
                onOpenChange={(open) => setOpenItem(item.label, open)}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={t(item.label)}
                      isActive={isChildActive}
                      className="data-[active=true]:bg-primary/5"
                    >
                      {Tag && <Tag />}
                      <span>{t(item.label)}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="h-[var(--radix-collapsible-content-height)] overflow-hidden transition-all duration-200 data-[state=closed]:h-0">
                    <SidebarMenuSub>
                      {item.childItems.map((subItem) =>
                        isSubGroup(subItem) ? (
                          <Collapsible
                            className="group/subcollapsible"
                            key={subItem.label}
                            open={isBranchOpen(
                              subGroupKey(item.label, subItem.label)
                            )}
                            onOpenChange={(open) =>
                              setOpenItem(
                                subGroupKey(item.label, subItem.label),
                                open
                              )
                            }
                          >
                            <SidebarMenuSubItem>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuSubButton
                                  className="justify-between data-[active=true]:bg-primary/10"
                                  isActive={subItem.childItems.some((leaf) =>
                                    isLinkActive(
                                      leaf.href,
                                      leaf.activePath,
                                      pathname,
                                      searchParams
                                    )
                                  )}
                                >
                                  <span>{t(subItem.label)}</span>
                                  <ChevronRight className="ml-auto shrink-0 transition-transform duration-200 group-data-[state=open]/subcollapsible:rotate-90" />
                                </SidebarMenuSubButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="h-[var(--radix-collapsible-content-height)] overflow-hidden transition-all duration-200 data-[state=closed]:h-0">
                                <SidebarMenuSub className="mx-0">
                                  {subItem.childItems.map((leaf) => (
                                    <SidebarMenuSubItem key={leaf.label}>
                                      <SidebarMenuSubButton
                                        asChild
                                        className="justify-between data-[active=true]:bg-primary/10"
                                        isActive={isLinkActive(
                                          leaf.href,
                                          leaf.activePath,
                                          pathname,
                                          searchParams
                                        )}
                                      >
                                        <Link
                                          to={leaf.href}
                                          target={leaf.target}
                                        >
                                          <span>{t(leaf.label)}</span>
                                          {isExternalLink(leaf.href) && (
                                            <SquareArrowOutUpRight className="ml-auto size-3.5 shrink-0 opacity-50" />
                                          )}
                                          {leaf.badge && (
                                            <SidebarMenuBadge
                                              className={cn(
                                                "rounded-full bg-primary/10 px-1.5 font-normal",
                                                leaf.badgeClassName
                                              )}
                                            >
                                              {leaf.badge}
                                            </SidebarMenuBadge>
                                          )}
                                        </Link>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  ))}
                                </SidebarMenuSub>
                              </CollapsibleContent>
                            </SidebarMenuSubItem>
                          </Collapsible>
                        ) : (
                          <SidebarMenuSubItem key={subItem.label}>
                            <SidebarMenuSubButton
                              asChild
                              className="justify-between data-[active=true]:bg-primary/10"
                              isActive={isLinkActive(
                                subItem.href,
                                subItem.activePath,
                                pathname,
                                searchParams
                              )}
                            >
                              <Link to={subItem.href} target={subItem.target}>
                                <span>{t(subItem.label)}</span>
                                {isExternalLink(subItem.href) && (
                                  <SquareArrowOutUpRight className="ml-auto size-3.5 shrink-0 opacity-50" />
                                )}
                                {subItem.badge && (
                                  <SidebarMenuBadge
                                    className={cn(
                                      "rounded-full bg-primary/10 px-1.5 font-normal",
                                      subItem.badgeClassName
                                    )}
                                  >
                                    {subItem.badge}
                                  </SidebarMenuBadge>
                                )}
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            ) : (
              <SidebarMenuItem key={item.label}>
                <SidebarMenuButton
                  tooltip={t(item.label)}
                  asChild
                  isActive={pathname === item.href}
                  className="data-[active=true]:bg-primary/10"
                >
                  <Link to={item.href} target={item.target}>
                    {Tag && <Tag />}
                    <span>{t(item.label)}</span>
                    {isExternalLink(item.href) && (
                      <SquareArrowOutUpRight className="ml-auto size-3.5 shrink-0 opacity-50" />
                    )}
                  </Link>
                </SidebarMenuButton>
                {item.badge && (
                  <SidebarMenuBadge
                    className={cn(
                      "rounded-full bg-primary/10 px-1.5 font-normal",
                      item.badgeClassName
                    )}
                  >
                    {item.badge}
                  </SidebarMenuBadge>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

function SidebarVersionFooter({ isIconMode }: { isIconMode: boolean }) {
  const { t } = useTypedTranslation();
  const version = getDisplayAppVersion();
  if (!version) return null;

  const labeled = `${t("common.version")} ${version}`;

  return (
    <SidebarFooter>
      {isIconMode ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="flex cursor-default items-center justify-center py-1"
              aria-label={labeled}
            >
              <AppVersionLabel hideLabel className="text-center text-[10px]" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            {t("common.version")}{" "}
            <NoTranslate as="span">{version}</NoTranslate>
          </TooltipContent>
        </Tooltip>
      ) : (
        <AppVersionLabel className="px-2 py-1" />
      )}
    </SidebarFooter>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { t } = useTypedTranslation();
  const { state, isMobile, setOpenMobile } = useSidebar();

  const pathname = location.pathname;
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // Entrées masquées selon les rôles de realm (matrice §6.x du guide).
  const realmRoles = useRealmRoles();
  const visibleNavItems = useMemo(
    () => filterNavItemsByRoles(navItems, realmRoles),
    [realmRoles]
  );

  // Branches explicitement ouvertes/fermées par l'utilisateur (clés = clés i18n,
  // stables quelle que soit la langue). Sinon repli sur "contient la route active".
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const activeBranchKeys = useMemo(
    () => getActiveBranchKeys(visibleNavItems, pathname, searchParams),
    [visibleNavItems, pathname, searchParams]
  );

  const isBranchOpen = useCallback(
    (key: string) => openItems[key] ?? activeBranchKeys.has(key),
    [openItems, activeBranchKeys]
  );

  const setOpenItem = useCallback((key: string, open: boolean) => {
    setOpenItems((prev) => ({ ...prev, [key]: open }));
  }, []);

  // Seul le rail icône est trop étroit pour le sous-menu inline.
  const isIconMode = state === "collapsed" && !isMobile;

  // Overlay mobile : on referme au changement de route (tap sur un lien).
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  return (
    <Sidebar
      collapsible="icon"
      variant="sidebar"
      mobileTitle={t("layout.sidebar.mobileTitle")}
      mobileDescription={t("layout.sidebar.mobileDescription")}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="gap-2.5 [&>img]:size-8"
            >
              <Link to="/app">
                <img src="/flash_pay_logo.png" alt={t("app.name")} />
                <div className="flex flex-col items-start">
                  <NoTranslate
                    as="span"
                    className="text-nowrap text-lg font-semibold"
                  >
                    {t("app.name")}
                  </NoTranslate>
                  <span className="text-nowrap text-xs font-light text-muted-foreground">
                    {t("layout.sidebar.tagline")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="group-data-[collapsible=icon]:overflow-y-auto">
        {visibleNavItems.map((navGroup, index) => (
          <SidebarGroupedMenuItems
            key={navGroup.groupLabel || index}
            data={navGroup.items}
            groupLabel={navGroup.groupLabel}
            pathname={pathname}
            searchParams={searchParams}
            isIconMode={isIconMode}
            isBranchOpen={isBranchOpen}
            setOpenItem={setOpenItem}
            t={t}
          />
        ))}
      </SidebarContent>
      <SidebarVersionFooter isIconMode={isIconMode} />
    </Sidebar>
  );
}
